// AvatarPanel — sits at the top of the Vendor / Customer editor identity
// section. Shows the current avatar (or initials when missing), supports
// direct-to-S3 upload + delete via the /admin/(vendors|customers)/:id/avatar
// endpoints.

import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MediaUploader } from '@drivecars/ui';
import { Avatar } from '@drivecars/admin-ui';
import {
  deleteCustomerAvatar,
  deleteVendorAvatar,
  uploadCustomerAvatar,
  uploadVendorAvatar,
} from '@/lib/admin-api';

const MAX_AVATAR_BYTES = 5 * 1024 * 1024;

export interface AvatarPanelProps {
  /** Which endpoint base + role to target. */
  base: 'vendors' | 'customers';
  userId: number;
  /** Display name fallback when there's no avatar yet. */
  display: string;
  /** ISO timestamp shown as "Joined …" in the meta line. */
  joinedAt: string;
  /** Currently-uploaded avatar URL (absolute or `/api/media/file/…`). */
  avatarUrl: string | null;
}

export function AvatarPanel({
  base,
  userId,
  display,
  joinedAt,
  avatarUrl,
}: AvatarPanelProps) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  // Pick the right uploaders/deleters at the type level — these are thin
  // wrappers around the same factory.
  const uploadFn = base === 'vendors' ? uploadVendorAvatar : uploadCustomerAvatar;
  const deleteFn = base === 'vendors' ? deleteVendorAvatar : deleteCustomerAvatar;
  const queryKey = ['admin', base === 'vendors' ? 'vendors' : 'customers', userId];

  const invalidate = () => void queryClient.invalidateQueries({ queryKey });

  const uploadMut = useMutation({
    mutationFn: (file: File) => uploadFn(userId, file),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (e) => setError((e as Error).message),
  });

  const deleteMut = useMutation({
    mutationFn: () => deleteFn(userId),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (e) => setError((e as Error).message),
  });

  const busy = uploadMut.isPending || deleteMut.isPending;

  return (
    <div className="mb-4 space-y-3">
      <div className="flex items-center gap-3">
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt={display}
            className="h-12 w-12 rounded-full object-cover"
          />
        ) : (
          <Avatar name={display} size={48} />
        )}
        <div className="min-w-0 flex-1">
          <div className="truncate text-[14px] font-semibold">{display}</div>
          <div className="text-[12px] text-[color:var(--bo-mute)]">
            Joined {new Date(joinedAt).toLocaleDateString()}
          </div>
        </div>
        {avatarUrl ? (
          <button
            type="button"
            onClick={() => deleteMut.mutate()}
            disabled={busy}
            className="text-[11.5px] font-medium text-[#9f1239] hover:underline disabled:opacity-50"
          >
            Remove
          </button>
        ) : null}
      </div>
      {error ? (
        <div className="rounded-md border border-[#fecaca] bg-[#fff1f2] px-3 py-2 text-[12.5px] text-[#9f1239]">
          {error}
        </div>
      ) : null}
      <MediaUploader
        value={null}
        uploading={busy}
        maxSize={MAX_AVATAR_BYTES}
        onChange={(file) => {
          if (file) uploadMut.mutate(file);
        }}
        onError={(msg) => setError(msg)}
        className="min-h-[80px] !p-2"
      />
    </div>
  );
}
